import React from 'react';
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

interface BehavioralAnalysisProps {
    report: any;
}

const BehavioralAnalysis: React.FC<BehavioralAnalysisProps> = ({ report }) => {
    const { t } = useTranslation();

    // Risk Management calculations
    const profitFactor = report?.profit_factor ?? report?.risk_management?.profit_factor;
    const expectancy = report?.expectancy ?? report?.risk_management?.expectancy;
    const maxDrawdown = report?.risk_management?.max_drawdown;
    // API's max_drawdown_percent is measured against peak profit (e.g. 83% for a $1.1k dip on a 200k account);
    // use peak balance instead, same as the Metrix detail page
    const peakBalance = Number(report?.equity_analysis?.peak_balance) || 0;
    const maxDrawdownPercent = peakBalance > 0 && maxDrawdown != null
        ? (Math.abs(Number(maxDrawdown)) / peakBalance) * 100
        : report?.risk_management?.max_drawdown_percent;
    const sharpeRatio = report?.sharpe_ratio;
    const totalLots = report?.sum_volume;
    const avgVolume = report?.risk_management?.average_volume ?? (report?.total_trades && report?.sum_volume ? report.sum_volume / report.total_trades : 0);
    const maxVolume = report?.risk_management?.max_volume ?? 0;
    const minVolume = report?.risk_management?.min_volume ?? 0;

    // Behavioral Analysis calculations
    const behavioral = report?.behavioral_analysis || {};
    const flags = behavioral?.behavioral_flags || {};

    const isHighFreq = Boolean(flags?.high_frequency_trading);
    const isRevenge = Boolean(flags?.revenge_trading_detected);
    const isLongLoss = Boolean(flags?.long_losing_streaks);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 mt-4 sm:mt-6 gap-4 sm:gap-6">
            {/* Quản lý rủi ro */}
            <div className="bg-white rounded-[10px] border border-[#e5e9f0] p-3 sm:p-[18px_20px] shadow-[0_1px_2px_rgba(16,24,40,0.03)]">
                <h2 className="!text-[13.5px] !font-semibold !text-[#0a1a33] mb-[14px] flex items-center gap-1.5 m-0">
                    <Tooltip title={t('tooltip_RiskManagement', 'Quản lý rủi ro và các chỉ số nâng cao')}>
                        <span>{t('RiskManagement', 'Quản lý rủi ro')}</span>
                    </Tooltip>
                </h2>

                <div className="flex flex-col">
                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_profitfactor')}>
                            <span className="text-[#8a94a6] cursor-help">{t('ProfitFactor', 'Hệ số lợi nhuận (Profit Factor)')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">
                            {profitFactor !== undefined && profitFactor !== null ? Number(profitFactor).toFixed(2) : 'N/A'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_expectancy')}>
                            <span className="text-[#8a94a6] cursor-help">{t('Expectancy', 'Giá trị kỳ vọng')}</span>
                        </Tooltip>
                        <span className={`font-bold font-mono-num ${expectancy !== undefined && Number(expectancy) < 0 ? 'text-[#ef4444]' : 'text-[#14b8a6]'}`}>
                            {expectancy !== undefined && expectancy !== null ? Number(expectancy).toFixed(2) : 'N/A'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_risk_management_max_drawdown')}>
                            <span className="text-[#8a94a6] cursor-help">{t('MaxDrawdown', 'Giảm tối đa')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#ef4444] font-mono-num">
                            {maxDrawdown !== undefined && maxDrawdown !== null
                                ? `$${Number(maxDrawdown).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'N/A'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_risk_management_max_drawdown_percent')}>
                            <span className="text-[#8a94a6] cursor-help">{t('MaxDrawdownPercent', 'Giảm tối đa %')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#ef4444] font-mono-num">
                            {maxDrawdownPercent !== undefined && maxDrawdownPercent !== null
                                ? `${Number(maxDrawdownPercent).toFixed(2)}%`
                                : 'N/A'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_sharpe_ratio')}>
                            <span className="text-[#8a94a6] cursor-help">{t('SharpeRatio', 'Tỷ lệ Sharpe')}</span>
                        </Tooltip>
                        <span className={`font-bold font-mono-num ${sharpeRatio !== undefined && Number(sharpeRatio) < 0 ? 'text-[#ef4444]' : 'text-[#14b8a6]'}`}>
                            {sharpeRatio !== undefined && sharpeRatio !== null ? Number(sharpeRatio).toFixed(2) : 'N/A'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_sum_volume')}>
                            <span className="text-[#8a94a6] cursor-help">{t('TotalLots', 'Tổng số Lot')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">
                            {totalLots !== undefined && totalLots !== null ? Number(totalLots).toFixed(2) : '0.00'}
                        </span>
                    </div>

                    <div className="flex justify-between items-center gap-3 py-[9px] text-[13px]">
                        <Tooltip title={t('tooltip_volume_summary', 'Khối lượng Trung bình · Tối đa · Tối thiểu')}>
                            <span className="text-[#8a94a6] cursor-help">{t('VolumeRange', 'Khối lượng TB / Tối đa / Tối thiểu')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num whitespace-nowrap">
                            {`${Number(avgVolume).toFixed(2)} · ${Number(maxVolume).toFixed(2)} · ${Number(minVolume).toFixed(2)}`}
                        </span>
                    </div>
                </div>
            </div>

            {/* Phân tích hành vi */}
            <div className="bg-white rounded-[10px] border border-[#e5e9f0] p-3 sm:p-[18px_20px] shadow-[0_1px_2px_rgba(16,24,40,0.03)]">
                <h2 className="!text-[13.5px] !font-semibold !text-[#0a1a33] mb-[14px] flex items-center gap-1.5 m-0">
                    <Tooltip title={t('tooltip_BehavioralAnalysis', 'Phân tích hành vi giao dịch và các mẫu có thể ảnh hưởng kết quả.')}>
                        <span>{t('BehavioralAnalysis', 'Phân tích hành vi')}</span>
                    </Tooltip>
                </h2>

                <div className="flex flex-col">
                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_rapid_fire_trades')}>
                            <span className="text-[#8a94a6] cursor-help">{t('RapidFireTrades', 'Giao dịch nhanh')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">{behavioral.rapid_fire_trades ?? 0}</span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_revenge_trades')}>
                            <span className="text-[#8a94a6] cursor-help">{t('RevengeTrades', 'Giao dịch trả thù')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">{behavioral.revenge_trades ?? 0}</span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_max_consecutive_wins')}>
                            <span className="text-[#8a94a6] cursor-help">{t('MaxConsecutiveWins', 'Chiến thắng liên tiếp tối đa')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">{behavioral.max_consecutive_wins ?? 0}</span>
                    </div>

                    <div className="flex justify-between items-center py-[9px] border-b border-[#f1f3f7] text-[13px]">
                        <Tooltip title={t('tooltip_max_consecutive_losses')}>
                            <span className="text-[#8a94a6] cursor-help">{t('MaxConsecutiveLosses', 'Giao dịch thua nhiều nhất')}</span>
                        </Tooltip>
                        <span className="font-bold text-[#17233c] font-mono-num">{behavioral.max_consecutive_losses ?? 0}</span>
                    </div>

                    {/* Behavioral Flags */}
                    <div className="mt-3">
                        <div className="text-[11.5px] text-[#8a94a6] mb-2 uppercase tracking-[0.4px] font-semibold">
                            {t('BehavioralFlags', 'Cờ hành vi')}
                        </div>
                        <div className="space-y-1">
                            <div className="flex justify-between items-center text-[12.5px] py-[5px]">
                                <Tooltip title={t('tooltip_high_frequency_trading')}>
                                    <span className="text-[#17233c] cursor-help">{t('HighFrequencyTrading', 'Giao dịch tần suất cao')}</span>
                                </Tooltip>
                                <span className={`font-bold ${isHighFreq ? 'text-[#ef4444]' : 'text-[#8a94a6]'}`}>
                                    {isHighFreq ? 'Yes' : 'No'}
                                </span>
                            </div>

                            <div className="flex justify-between items-center text-[12.5px] py-[5px]">
                                <Tooltip title={t('tooltip_revenge_trading_detected')}>
                                    <span className="text-[#17233c] cursor-help">{t('RevengeTradingDetected', 'Phát hiện giao dịch trả thù')}</span>
                                </Tooltip>
                                <span className={`font-bold ${isRevenge ? 'text-[#ef4444]' : 'text-[#8a94a6]'}`}>
                                    {isRevenge ? 'Yes' : 'No'}
                                </span>
                            </div>

                            <div className="flex justify-between items-center text-[12.5px] py-[5px]">
                                <Tooltip title={t('tooltip_long_losing_streaks')}>
                                    <span className="text-[#17233c] cursor-help">{t('LongLosingStreaks', 'Chối thua dai')}</span>
                                </Tooltip>
                                <span className={`font-bold ${isLongLoss ? 'text-[#ef4444]' : 'text-[#8a94a6]'}`}>
                                    {isLongLoss ? 'Yes' : 'No'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BehavioralAnalysis;